import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
    Ticket,
    Clock,
    CheckCircle,
    RefreshCcw,
    Paperclip,
    ClipboardList,
    User,
    Calendar,
    Filter,
    X,
    MessageSquare,
    Package
} from 'lucide-react';

const OwnerTickets = () => {
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Filters
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [dateFilter, setDateFilter] = useState('');

    // Modal state
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [adminResponse, setAdminResponse] = useState('');
    const [newStatus, setNewStatus] = useState('');
    const [saving, setSaving] = useState(false);

    const categories = [
        'Quality Issues',
        'Pricing Issues',
        'Delivery Issues',
        'Product Issues',
        'Other'
    ];

    const statuses = ['raised', 'processing', 'closed'];

    const fetchTickets = useCallback(async () => {
        try {
            setLoading(true);
            const formData = new FormData();
            if (categoryFilter !== 'all') formData.append('category', categoryFilter);
            if (statusFilter !== 'all') formData.append('status', statusFilter);
            if (dateFilter) formData.append('date', dateFilter);

            const response = await axios.post('https://rotica.in/get_support_tickets.php', formData);

            if (response.data.success) {
                setTickets(response.data.tickets || []);
                setError('');
            } else {
                setError(response.data.message || 'Failed to fetch tickets');
            }
        } catch (err) {
            console.error(err);
            setError('Network error: Could not fetch tickets');
        } finally {
            setLoading(false);
        }
    }, [categoryFilter, statusFilter, dateFilter]);

    useEffect(() => {
        fetchTickets();
    }, [fetchTickets]);

    const openTicketModal = (ticket) => {
        setSelectedTicket(ticket);
        setAdminResponse(ticket.admin_response || '');
        setNewStatus(ticket.status);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedTicket(null);
        setAdminResponse('');
        setNewStatus('');
    };

    const handleSave = async () => {
        if (!selectedTicket) return;

        setSaving(true);
        try {
            const formData = new FormData();
            formData.append('ticket_id', selectedTicket.id);
            formData.append('admin_response', adminResponse);
            formData.append('status', newStatus);

            const response = await axios.post('https://rotica.in/update_ticket.php', formData);

            if (response.data.success) {
                closeModal();
                fetchTickets();
            } else {
                alert('Update failed: ' + response.data.message);
            }
        } catch (err) {
            console.error(err);
            alert('Network error during update');
        } finally {
            setSaving(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusClass = (status) => {
        switch (status) {
            case 'raised': return 'ticket-status-raised';
            case 'processing': return 'ticket-status-processing';
            case 'closed': return 'ticket-status-closed';
            default: return '';
        }
    };

    const truncateText = (text, maxLength = 50) => {
        if (!text) return '-';
        return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    };

    const getAttachmentUrl = (url) => {
        if (!url) return null;
        if (url.startsWith('http')) return url;
        return `https://rotica.in/${url}`;
    };

    return (
        <div className="dashboard-container">
            <div className="main-content">
                <header className="page-header">
                    <h1>Customer Support Tickets</h1>
                    <p className="subtitle">Manage and respond to customer grievances</p>
                </header>

                {/* Stats Cards */}
                <div className="stats-cards">
                    <div className="stat-card" style={{ borderLeft: '4px solid #e74c3c' }}>
                        <div className="stat-icon"><Ticket size={24} color="#e74c3c" /></div>
                        <div className="stat-info">
                            <h3>Total Tickets</h3>
                            <p>{tickets.length}</p>
                        </div>
                    </div>
                    <div className="stat-card" style={{ borderLeft: '4px solid #f39c12' }}>
                        <div className="stat-icon"><Clock size={24} color="#f39c12" /></div>
                        <div className="stat-info">
                            <h3>Raised</h3>
                            <p>{tickets.filter(t => t.status === 'raised').length}</p>
                        </div>
                    </div>
                    <div className="stat-card" style={{ borderLeft: '4px solid #3498db' }}>
                        <div className="stat-icon"><RefreshCcw size={24} color="#3498db" /></div>
                        <div className="stat-info">
                            <h3>Processing</h3>
                            <p>{tickets.filter(t => t.status === 'processing').length}</p>
                        </div>
                    </div>
                    <div className="stat-card" style={{ borderLeft: '4px solid #27ae60' }}>
                        <div className="stat-icon"><CheckCircle size={24} color="#27ae60" /></div>
                        <div className="stat-info">
                            <h3>Closed</h3>
                            <p>{tickets.filter(t => t.status === 'closed').length}</p>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="tickets-filters">
                    <div className="filter-group">
                        <label>Category</label>
                        <select
                            value={categoryFilter}
                            onChange={(e) => setCategoryFilter(e.target.value)}
                        >
                            <option value="all">All Categories</option>
                            {categories.map(cat => (
                                <option key={cat} value={cat}>{cat}</option>
                            ))}
                        </select>
                    </div>
                    <div className="filter-group">
                        <label>Status</label>
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <option value="all">All Status</option>
                            {statuses.map(s => (
                                <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                            ))}
                        </select>
                    </div>
                    <div className="filter-group">
                        <label>Date</label>
                        <input
                            type="date"
                            value={dateFilter}
                            onChange={(e) => setDateFilter(e.target.value)}
                        />
                    </div>
                    {(categoryFilter !== 'all' || statusFilter !== 'all' || dateFilter) && (
                        <button
                            className="clear-filters-btn"
                            onClick={() => {
                                setCategoryFilter('all');
                                setStatusFilter('all');
                                setDateFilter('');
                            }}
                        >
                            Clear Filters
                        </button>
                    )}
                </div>

                {/* Tickets Table - Desktop */}
                <div className="tickets-section">
                    {loading && <p className="loading-text">Fetching tickets...</p>}
                    {error && <p className="error-text">{error}</p>}

                    {!loading && !error && (
                        <>
                            {/* Desktop Table */}
                            <div className="table-wrapper tickets-table-desktop">
                                <table className="orders-table tickets-table">
                                    <thead>
                                        <tr>
                                            <th>ID</th>
                                            <th>Category</th>
                                            <th>Subcategory</th>
                                            <th>Description</th>
                                            <th>Status</th>
                                            <th>Created</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {tickets.length > 0 ? (
                                            tickets.map((ticket, index) => (
                                                <tr
                                                    key={ticket.id}
                                                    onClick={() => openTicketModal(ticket)}
                                                    className="ticket-row"
                                                    style={{ animationDelay: `${index * 0.05}s` }}
                                                >
                                                    <td>#{ticket.id}</td>
                                                    <td>
                                                        <span className="ticket-category-badge">
                                                            {ticket.category}
                                                        </span>
                                                    </td>
                                                    <td>{ticket.subcategory || '-'}</td>
                                                    <td className="ticket-description">
                                                        {truncateText(ticket.description, 40)}
                                                    </td>
                                                    <td>
                                                        <span className={`ticket-status-badge ${getStatusClass(ticket.status)}`}>
                                                            {ticket.status}
                                                        </span>
                                                    </td>
                                                    <td>{formatDate(ticket.created_at)}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="6" className="empty-state">
                                                    No tickets found
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile Cards */}
                            <div className="tickets-cards-mobile">
                                {tickets.length > 0 ? (
                                    tickets.map((ticket, index) => (
                                        <div
                                            key={ticket.id}
                                            className="ticket-card"
                                            onClick={() => openTicketModal(ticket)}
                                            style={{ animationDelay: `${index * 0.05}s` }}
                                        >
                                            <div className="ticket-card-header">
                                                <span className="ticket-id">#{ticket.id}</span>
                                                <span className={`ticket-status-badge ${getStatusClass(ticket.status)}`}>
                                                    {ticket.status}
                                                </span>
                                            </div>
                                            <div className="ticket-card-category">
                                                <span className="ticket-category-badge">{ticket.category}</span>
                                                {ticket.subcategory && (
                                                    <span className="ticket-subcategory">{ticket.subcategory}</span>
                                                )}
                                            </div>
                                            <p className="ticket-card-description">
                                                {truncateText(ticket.description, 80)}
                                            </p>
                                            <div className="ticket-card-footer">
                                                <span className="ticket-date">{formatDate(ticket.created_at)}</span>
                                                <span className="ticket-tap-hint">Tap to view →</span>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="empty-state-card">
                                        <Ticket size={48} color="#86868b" />
                                        <p>No tickets found</p>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Ticket Detail Modal */}
            {showModal && selectedTicket && (
                <div className="ticket-modal-overlay" onClick={closeModal}>
                    <div className="ticket-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="ticket-modal-header">
                            <h2>Ticket #{selectedTicket.id}</h2>
                            <button className="modal-close-btn" onClick={closeModal}><X size={20} /></button>
                        </div>

                        <div className="ticket-modal-body">
                            {/* Attachment Section */}
                            {selectedTicket.attachment_url && (
                                <div className="ticket-attachment-section">
                                    <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <Paperclip size={18} /> Attachment
                                    </h3>
                                    <div className="ticket-attachment">
                                        <img
                                            src={getAttachmentUrl(selectedTicket.attachment_url)}
                                            alt="Ticket attachment"
                                            onClick={() => window.open(getAttachmentUrl(selectedTicket.attachment_url), '_blank')}
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Ticket Info */}
                            <div className="ticket-info-section">
                                <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <ClipboardList size={18} /> Ticket Details
                                </h3>
                                <div className="ticket-info-grid">
                                    <div className="info-item">
                                        <label>Category</label>
                                        <span className="ticket-category-badge">{selectedTicket.category}</span>
                                    </div>
                                    <div className="info-item">
                                        <label>Subcategory</label>
                                        <span>{selectedTicket.subcategory || '-'}</span>
                                    </div>
                                    <div className="info-item full-width">
                                        <label>Description</label>
                                        <p className="ticket-full-description">{selectedTicket.description}</p>
                                    </div>
                                    <div className="info-item">
                                        <label>User Phone</label>
                                        <span>{selectedTicket.user_phone}</span>
                                    </div>
                                    <div className="info-item">
                                        <label>Created</label>
                                        <span>{formatDate(selectedTicket.created_at)}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Order Info */}
                            {selectedTicket.order_id && (
                                <div className="ticket-order-section">
                                    <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <Package size={18} /> Order Information
                                    </h3>
                                    <div className="ticket-info-grid">
                                        <div className="info-item">
                                            <label>Order ID</label>
                                            <span className="order-id-badge">#{selectedTicket.order_id}</span>
                                        </div>
                                        <div className="info-item">
                                            <label>Customer</label>
                                            <span>{selectedTicket.customer_name || '-'}</span>
                                        </div>
                                        <div className="info-item">
                                            <label>Assigned To</label>
                                            <span className={`assigned-badge ${selectedTicket.assigned_type || 'unassigned'}`}>
                                                {selectedTicket.assigned_name || 'Unassigned'}
                                            </span>
                                        </div>
                                        {selectedTicket.order_items && selectedTicket.order_items.length > 0 && (
                                            <div className="info-item full-width">
                                                <label>Order Items</label>
                                                <div className="order-items-list">
                                                    {selectedTicket.order_items.map((item, idx) => (
                                                        <span key={idx} className="order-item-badge">
                                                            {item.name} × {item.quantity}
                                                            {item.pack_size > 1 && ` (Pack of ${item.pack_size})`}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Admin Response Section */}
                            <div className="ticket-response-section">
                                <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <MessageSquare size={18} /> Admin Response
                                </h3>
                                <textarea
                                    className="admin-response-textarea"
                                    placeholder="Enter your response to the customer..."
                                    value={adminResponse}
                                    onChange={(e) => setAdminResponse(e.target.value)}
                                    rows={4}
                                />
                            </div>

                            {/* Status Update */}
                            <div className="ticket-status-section">
                                <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <RefreshCcw size={18} /> Update Status
                                </h3>
                                <div className="status-selector">
                                    {statuses.map(s => (
                                        <button
                                            key={s}
                                            className={`status-option ${newStatus === s ? 'active' : ''} ${getStatusClass(s)}`}
                                            onClick={() => setNewStatus(s)}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                                                {s === 'raised' && <Clock size={14} />}
                                                {s === 'processing' && <RefreshCcw size={14} />}
                                                {s === 'closed' && <CheckCircle size={14} />}
                                                {s.charAt(0).toUpperCase() + s.slice(1)}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="ticket-modal-footer">
                            <button className="cancel-btn" onClick={closeModal} disabled={saving}>
                                Cancel
                            </button>
                            <button className="save-btn" onClick={handleSave} disabled={saving}>
                                {saving ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OwnerTickets;
